/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * Which repos a scoped grant may target, per org.
 *
 * The backend reads the repository categories out of repos/<org>.tf and returns
 * EVERY unarchived repo, each marked grantable or not. Uncategorized repos are
 * present-and-disabled by design: a picker that silently omitted them would leave
 * someone typing "svc-test" concluding the field is broken, when the truth is
 * that the repo needs a team_repository_attachments grant written by hand.
 */
import { useEffect, useState } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

export type GrantableRepo = {
  name: string;
  grantable: boolean;
  /** Present exactly when grantable is false: why, and what to do instead. */
  reason?: string;
};

export type GrantableRepos = {
  repos: GrantableRepo[];
  /** Category fragments the backend could not resolve (they compute at plan time). */
  computedTails: number;
};

/**
 * Validate the response body rather than casting it.
 *
 * Same posture as the cart-restore path: this data steers what a user believes
 * they can stage against production Terraform, so a malformed body degrades to
 * null (the picker stays in its unavailable state) instead of rendering garbage
 * rows that stage cleanly and fail at dry-run.
 */
export const parseGrantableRepos = (body: unknown): GrantableRepos | null => {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as { repos?: unknown; computedTails?: unknown };
  if (!Array.isArray(b.repos)) return null;
  if (typeof b.computedTails !== 'number' || !Number.isFinite(b.computedTails)) {
    return null;
  }
  const repos: GrantableRepo[] = [];
  for (const r of b.repos) {
    if (typeof r !== 'object' || r === null) return null;
    const e = r as { name?: unknown; grantable?: unknown; reason?: unknown };
    if (typeof e.name !== 'string' || !e.name) return null;
    if (typeof e.grantable !== 'boolean') return null;
    if (e.reason !== undefined && typeof e.reason !== 'string') return null;
    repos.push({
      name: e.name,
      grantable: e.grantable,
      ...(typeof e.reason === 'string' ? { reason: e.reason } : {}),
    });
  }
  return { repos, computedTails: b.computedTails };
};

/**
 * The cart label for a scoped grant. The repos are IN the label on purpose:
 * "Set m42-it repo access → push" reads identically whether it means one repo or
 * seventy, and that ambiguity is how an org-wide grant reached a PR believing it
 * was scoped. Long selections collapse to a count after naming the first few.
 */
export const grantLabel = (
  slug: string,
  permission: string,
  repos: string[],
): string => {
  const shown = repos.slice(0, 3).join(', ');
  const more = repos.length - 3;
  return `Grant ${slug} ${permission} on ${shown}${
    more > 0 ? ` and ${more} more` : ''
  }`;
};

/**
 * One fetch per org per session. The list changes when someone edits repos/*.tf,
 * which is rare enough that Refresh-the-page is the honest invalidation story;
 * a TTL here would only pretend otherwise.
 */
const cache = new Map<string, GrantableRepos>();

export function useGrantableRepos(
  org: string,
  enabled: boolean,
): { data?: GrantableRepos; error?: string } {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<{ data?: GrantableRepos; error?: string }>(
    () => (cache.has(org) ? { data: cache.get(org) } : {}),
  );

  useEffect(() => {
    if (!enabled || cache.has(org)) return undefined;
    let active = true;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('team-admin');
        const res = await fetchApi.fetch(
          `${base}/teams/${encodeURIComponent(org)}/repos`,
        );
        if (!res.ok) throw new Error(`request failed (${res.status})`);
        const parsed = parseGrantableRepos(await res.json());
        if (!parsed) throw new Error('malformed repository list');
        cache.set(org, parsed);
        if (active) setState({ data: parsed });
      } catch (err) {
        if (active) {
          setState({
            error: `Repository list unavailable: ${
              err instanceof Error ? err.message : String(err)
            }`,
          });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [org, enabled, discoveryApi, fetchApi]);

  return state;
}
